import React from 'react';
import { Col, Row, Dropdown, Menu, Tooltip, Radio } from 'antd';
import {
  RightOutlined,
  DownOutlined,
  LeftOutlined,
  FolderOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import {
  ThemeButton,
  ThemeForm,
  ThemeInput,
  ThemeModal,
} from '../pages/Styles/Theme';
import {
  FolderSectionLabel,
  FolderBackNav as FolderBackNavStyled,
  ThemeFolderCard,
  FolderListScrollable,
  FolderRadioGroup,
} from '../pages/Projects/ProjectsStyle';

/**
 * Back navigation bar shown when inside a folder.
 */
export const FolderBackNav = ({ currentFolderName, onBack }) => (
  <FolderBackNavStyled>
    <span className="back-arrow" onClick={onBack}><LeftOutlined /></span>
    <Tooltip
      title={currentFolderName.length > 25 ? currentFolderName : ''}
      overlayStyle={{ fontSize: 12, maxWidth: 250 }}
      overlayInnerStyle={{ fontSize: 12, padding: '4px 8px' }}
    >
      <span className="folder-name">{currentFolderName}</span>
    </Tooltip>
  </FolderBackNavStyled>
);

/**
 * Folder cards section (grid of folder cards with collapse toggle).
 * Used in both card view and table view at root level.
 */
export const FolderCardsSection = ({
  filteredFolders,
  foldersCollapsed,
  setFoldersCollapsed,
  onFolderClick,
  onRenameFolderClick,
  onDeleteFolderClick,
  // Drag props (optional — only used in card view at root)
  dragOverFolderId,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  enableDrag = false,
}) => {
  if (filteredFolders.length === 0) return null;

  const renderFolderMenu = (folder) => (
    <Menu
      selectable={false}
      onClick={({ key, domEvent }) => {
        domEvent.stopPropagation();
        if (key === 'rename') onRenameFolderClick(folder);
        else if (key === 'delete') onDeleteFolderClick(folder);
      }}
    >
      <Menu.Item key="rename">Rename</Menu.Item>
      <Menu.Divider />
      <Menu.Item key="delete">Delete</Menu.Item>
    </Menu>
  );

  return (
    <>
      <FolderSectionLabel>
        <img src="/images/folder-open-gray.svg" alt="Folders" />
        Folders
        <span className="collapse-arrow" onClick={() => setFoldersCollapsed(!foldersCollapsed)} style={{ cursor: 'pointer' }}>
          {foldersCollapsed ? <RightOutlined /> : <DownOutlined />}
        </span>
      </FolderSectionLabel>
      {!foldersCollapsed && (
        <Row gutter={[16, 16]} className="mb-6">
          {filteredFolders.map((folder) => (
            <Col span={24} sm={12} md={12} lg={12} xl={8} xxl={6} xxxl={4} key={folder._id}>
              <ThemeFolderCard
                className={enableDrag && dragOverFolderId === folder._id ? 'drag-over' : ''}
                onClick={() => onFolderClick(folder)}
                onDragOver={enableDrag ? onDragOver : undefined}
                onDragEnter={enableDrag ? (e) => onDragEnter(e, folder._id) : undefined}
                onDragLeave={enableDrag ? (e) => onDragLeave(e, folder._id) : undefined}
                onDrop={enableDrag ? (e) => onDrop(e, folder._id) : undefined}
              >
                <div className="folder-icon-wrapper">
                  <img
                    src={enableDrag && dragOverFolderId === folder._id ? '/images/folder-open-purple.svg' : '/images/folder-filled-purple.svg'}
                    alt="Folder"
                  />
                </div>
                <div className="folder-name">{folder.name}</div>
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  dropdownRender={() => renderFolderMenu(folder)}
                >
                  <div className="folder-menu" onClick={(e) => e.stopPropagation()}>
                    <img src="/images/menu-dots-vertical.svg" alt="Menu" />
                  </div>
                </Dropdown>
              </ThemeFolderCard>
            </Col>
          ))}
        </Row>
      )}
    </>
  );
};

/**
 * Items section label (shown between folders and items when both exist).
 */
export const ItemsSectionLabel = ({
  label,
  itemsCollapsed,
  setItemsCollapsed,
}) => (
  <FolderSectionLabel>
    <AppstoreOutlined />
    {label}
    <span className="collapse-arrow" onClick={() => setItemsCollapsed(!itemsCollapsed)} style={{ cursor: 'pointer' }}>
      {itemsCollapsed ? <RightOutlined /> : <DownOutlined />}
    </span>
  </FolderSectionLabel>
);

/**
 * New Folder button (desktop and mobile variants).
 */
export const NewFolderButton = ({ isMobile, onClick }) => {
  if (isMobile) {
    return (
      <span
        className="background"
        style={{ cursor: 'pointer' }}
        onClick={onClick}
      >
        <img src="/images/folder-open-purple.svg" alt="Folder" style={{ width: 18, height: 18 }} />
      </span>
    );
  }
  return (
    <ThemeButton
      size="large"
      className="noBackground"
      icon={<img src="/images/folder-open-purple.svg" alt="Folder" style={{ width: 18, height: 18 }} />}
      style={{ borderColor: 'rgba(61, 41, 105, 0.2)', color: '#3D2969' }}
      onClick={onClick}
    >
      New Folder
    </ThemeButton>
  );
};

/**
 * All folder-related modals: Create, Rename, Delete, Move, Remove from folder.
 */
export const FolderModals = ({
  // Create folder
  createFolderModal,
  setCreateFolderModal,
  folderForm,
  handleCreateFolder,
  createFolderState,
  // Rename folder
  renameFolderModal,
  setRenameFolderModal,
  folderToRename,
  renameFolderForm,
  handleRenameFolder,
  renameFolderState,
  // Delete folder
  deleteFolderModal,
  setDeleteFolderModal,
  folderToDelete,
  handleDeleteFolder,
  deleteFolderState,
  // Remove from folder
  removeFromFolderModal,
  setRemoveFromFolderModal,
  itemToRemove,
  confirmRemoveFromFolder,
  removeItemFromFolderState,
  // Move to folder
  moveToFolderModal,
  setMoveToFolderModal,
  itemToMove,
  selectedFolderId,
  setSelectedFolderId,
  handleMoveToFolder,
  addItemToFolderState,
  folders,
  currentFolderId,
  // Labels
  itemLabel = 'item',
}) => {
  const capitalizedLabel = itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1);

  return (
    <>
      {/* Create Folder Modal */}
      <ThemeModal
        title="Create New Folder"
        open={createFolderModal}
        onCancel={() => {
          setCreateFolderModal(false);
          folderForm.resetFields();
        }}
        centered
        footer={null}
        width={550}
      >
        <ThemeForm layout="vertical" form={folderForm}>
          <Row>
            <Col xs={24}>
              <ThemeForm.Item
                className="labelStyle1"
                label="Folder Name"
                name="folderName"
                rules={[{ required: true, message: 'Please enter folder name' }]}
              >
                <ThemeInput
                  placeholder="Enter folder name"
                  size="large"
                />
              </ThemeForm.Item>
            </Col>
          </Row>
        </ThemeForm>
        <div className="text-end mt-3">
          <ThemeButton
            size="large"
            className="noBackground mr-3"
            onClick={() => {
              setCreateFolderModal(false);
              folderForm.resetFields();
            }}
            disabled={createFolderState.apiState === 'loading'}
          >
            Cancel
          </ThemeButton>
          <ThemeButton
            size="large"
            onClick={handleCreateFolder}
            loading={createFolderState.apiState === 'loading'}
          >
            Create Folder
          </ThemeButton>
        </div>
      </ThemeModal>

      {/* Rename Folder Modal */}
      <ThemeModal
        title="Rename Folder"
        open={renameFolderModal}
        onCancel={() => {
          setRenameFolderModal(false);
          renameFolderForm.resetFields();
        }}
        centered
        footer={null}
        width={550}
      >
        <ThemeForm layout="vertical" form={renameFolderForm}>
          <Row>
            <Col xs={24}>
              <ThemeForm.Item
                className="labelStyle1"
                label="Folder Name"
                name="folderName"
                rules={[{ required: true, message: 'Please enter folder name' }]}
              >
                <ThemeInput
                  placeholder="Enter folder name"
                  size="large"
                />
              </ThemeForm.Item>
            </Col>
          </Row>
        </ThemeForm>
        <div className="text-end mt-3">
          <ThemeButton
            size="large"
            className="noBackground mr-3"
            onClick={() => {
              setRenameFolderModal(false);
              renameFolderForm.resetFields();
            }}
            disabled={renameFolderState.apiState === 'loading'}
          >
            Cancel
          </ThemeButton>
          <ThemeButton
            size="large"
            onClick={handleRenameFolder}
            loading={renameFolderState.apiState === 'loading'}
          >
            Rename
          </ThemeButton>
        </div>
      </ThemeModal>

      {/* Delete Folder Modal */}
      <ThemeModal
        title="Delete Folder"
        open={deleteFolderModal}
        onOk={handleDeleteFolder}
        onCancel={() => {
          setDeleteFolderModal(false);
        }}
        confirmLoading={deleteFolderState.apiState === 'loading'}
        okText="Delete"
        cancelText="Cancel"
        centered
      >
        <p>{`Are you sure you want to delete folder "${folderToDelete?.name}"?`}</p>
        <p>{`${capitalizedLabel}s inside this folder will be moved back to the root.`}</p>
      </ThemeModal>

      {/* Remove from Folder Confirmation Modal */}
      <ThemeModal
        title="Remove from Folder"
        open={removeFromFolderModal}
        onOk={confirmRemoveFromFolder}
        onCancel={() => {
          setRemoveFromFolderModal(false);
        }}
        confirmLoading={removeItemFromFolderState.apiState === 'loading'}
        okText="Remove"
        cancelText="Cancel"
        centered
      >
        <p>{`Are you sure you want to remove "${itemToRemove?.name}" from this folder?`}</p>
        <p>{`The ${itemLabel} will be moved back to the root.`}</p>
      </ThemeModal>

      {/* Move to Folder Modal */}
      <ThemeModal
        title="Move to Folder"
        open={moveToFolderModal}
        onCancel={() => {
          setMoveToFolderModal(false);
          setSelectedFolderId(null);
        }}
        centered
        footer={null}
        width={550}
      >
        {folders.filter((f) => f._id !== currentFolderId).length === 0 && !currentFolderId ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            No folders found. Create a folder first.
          </div>
        ) : (
          <FolderListScrollable>
            <FolderRadioGroup
              onChange={(e) => setSelectedFolderId(e.target.value)}
              value={selectedFolderId}
            >
              {currentFolderId && (
                <Radio key="__root__" value="__root__">
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AppstoreOutlined />
                    Root (No Folder)
                  </span>
                </Radio>
              )}
              {folders.filter((f) => f._id !== currentFolderId).map((folder) => (
                <Radio key={folder._id} value={folder._id}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FolderOutlined />
                    {folder.name}
                  </span>
                </Radio>
              ))}
            </FolderRadioGroup>
          </FolderListScrollable>
        )}
        <div className="text-end mt-3">
          <ThemeButton
            size="large"
            className="noBackground mr-3"
            onClick={() => {
              setMoveToFolderModal(false);
              setSelectedFolderId(null);
            }}
            disabled={addItemToFolderState.apiState === 'loading'}
          >
            Cancel
          </ThemeButton>
          <ThemeButton
            size="large"
            onClick={handleMoveToFolder}
            loading={addItemToFolderState.apiState === 'loading'}
            disabled={!selectedFolderId}
          >
            Move
          </ThemeButton>
        </div>
      </ThemeModal>
    </>
  );
};

/**
 * Folder selector dropdown for create-item modals.
 * Shows a dropdown to select which folder to put the new item in.
 */
export const FolderSelector = ({
  folders,
  currentFolderId,
  currentFolderName,
  createItemFolderId,
  setCreateItemFolderId,
  XThemeSelect,
}) => {
  if (folders.length === 0 && !currentFolderId) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 13, fontWeight: 500, color: '#1f2430', marginBottom: 8 }}>
        Folder
      </div>
      <Tooltip
        title={currentFolderId ? `You are inside "${currentFolderName}" folder. The item will be created in this folder.` : ''}
        placement="top"
      >
        <div>
          <XThemeSelect
            placeholder={
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FolderOutlined />
                Select Folder
              </span>
            }
            size="large"
            style={{ width: '100%' }}
            allowClear={!currentFolderId}
            disabled={!!currentFolderId}
            value={createItemFolderId}
            onChange={(value) => setCreateItemFolderId(value)}
          >
            {folders.map((folder) => (
              <XThemeSelect.Option key={folder._id} value={folder._id}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FolderOutlined />
                  {folder.name}
                </span>
              </XThemeSelect.Option>
            ))}
          </XThemeSelect>
        </div>
      </Tooltip>
    </div>
  );
};
